import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import { NOTICE_CORPORATE_CONTACT_CATEGORY_CD } from '../../components/controller/notice-corporate/composables/b15JurisdictionDisplay';

export const noticeCorporateControllerFlow: ChatFlow = {
  id: 'sg-controller-notice-corporate',
  taskCodeMatch: ['B32107', 'B32007'],
  categoryMatch: [
    'notice for controller corporate',
    'notice for controllers corporate',
    'controller notice corporate',
    'notice controller corporate',
    'controller (corporate)',
    '386ag corporate',
    '386ag(2)(a) corporate',
    'corporate controller notice',
  ],

  getInitialMessages(preSet): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}** (Notice for Controllers 386AG(2)(a) — Corporate).\n\n` +
          (docCount > 0 ? `This preset includes ${docCount} document(s).\n\n` : '') +
          `Enter a **Task Title**, select the **Singapore company**, then complete the notice and corporate controller details on the right.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **Singapore company** issuing this controller notice.',
      widget: 'entity-selector',
      widgetConfig: { targetVarKey: '_entity', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Fill in notice date, reply address, and corporate controller details on the right. Use **Lookup from chat** to pick contacts. Click **Create Task** when ready.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'jurisdiction-b15',
      aiMessage: 'Select **jurisdiction** (B15) for the corporate controller.',
      widget: 'cm-base-code-list',
      widgetConfig: { targetVarKey: '_jurisdictionB15', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'country-code-b05',
      aiMessage:
        'Select **country phone code** for the corporate controller. Search by country name (e.g. Korea) or dial code (e.g. **82**).',
      widget: 'cm-base-code-list',
      widgetConfig: { targetVarKey: '_countryB05', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-controller',
      aiMessage: 'Select a **global contact** for this corporate controller.',
      widget: 'officer-list',
      widgetConfig: { targetVarKey: '_controller', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-secretary',
      aiMessage: 'Select the company **secretary** for notice signer.',
      widget: 'officer-list',
      widgetConfig: { targetVarKey: '_secretary', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-ack-signer',
      aiMessage: 'Select the **acknowledgement signer** from all officers of this entity.',
      widget: 'officer-list',
      widgetConfig: { targetVarKey: '_ackSigner', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'address-list',
      aiMessage: 'This entity has multiple addresses. Pick the one to use for **Reply-to address**.',
      widget: 'address-options',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Please enter the **Reply-to address** (Singapore). It will be filled back to the form automatically.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
          companyAddress: ent.companyAddress || ent.address || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const fg = String(ctx.fieldGroup || '');

      if (fg === 'controllerJurisdictionB15') {
        return {
          messages: [
            {
              role: 'user',
              content: 'Opening **jurisdiction** list...',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'jurisdiction-b15',
          nextStepWidgetData: {
            baseCodeCountry: 'CM',
            baseCodeMain: 'B15',
            baseCodeListLabel: 'Jurisdiction — please select',
          },
        };
      }

      if (fg === 'controllerCountryB05') {
        return {
          messages: [
            {
              role: 'user',
              content: 'Opening **country phone code** list...',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'country-code-b05',
          nextStepWidgetData: {
            baseCodeCountry: 'CM',
            baseCodeMain: 'B05',
            baseCodeListLabel: 'Country Phone Code — please select',
          },
        };
      }

      const entityCd = context.entityCd || context.formData.entityCd || context.formData.icpCd || '';
      if (!entityCd) {
        return {
          messages: [
            { role: 'ai', content: 'Please select an entity first.', timestamp: Date.now(), stepId: 1, source: 'system' },
          ],
        };
      }

      if (fg === 'noticeSigner') {
        return {
          messages: [
            { role: 'user', content: 'Looking up **notice signer** (company secretary)...', timestamp: Date.now(), stepId: 1 },
          ],
          nextStepId: 'select-secretary',
          nextStepWidgetData: { entityCd, selectedRole: ctx.defaultRole || 'SECRETARY' },
        };
      }

      if (fg === 'acknowledgementSigner') {
        return {
          messages: [
            {
              role: 'user',
              content: 'Looking up **acknowledgement signer** (all officers for this entity)...',
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'select-ack-signer',
          nextStepWidgetData: { entityCd, selectedRole: 'ENTITY_OFFICER' },
        };
      }

      if (fg !== 'corporateController' && fg !== 'noticeRecipient') return {};

      const lookupLabel =
        fg === 'noticeRecipient' ? 'notice recipient (global contact)' : 'corporate controller';

      return {
        messages: [
          { role: 'user', content: `Looking up **${lookupLabel}**...`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'select-controller',
        nextStepWidgetData: {
          entityCd,
          selectedRole: ctx.defaultRole || 'CONTACT',
          contactCategoryCd: NOTICE_CORPORATE_CONTACT_CATEGORY_CD,
        },
      };
    }

    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const fg = String(ctx.fieldGroup || '');

      if (fg === 'controllerRegisteredOffice' || fg === 'controllerAlternateAddress') {
        const label = ctx.subjectLabel || (fg === 'controllerAlternateAddress'
          ? 'Alternate address'
          : 'Registered office address');
        if (ctx.directInput) {
          return {
            messages: [
              {
                role: 'user',
                content: `Enter **${label}** (Local Singapore or Foreign).`,
                timestamp: Date.now(),
                stepId: 1,
              },
            ],
            nextStepId: 'address-input',
          };
        }
        const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
        if (opts.length <= 1) {
          return {
            messages: [
              {
                role: 'user',
                content: `Enter **${label}** (Local Singapore or Foreign).`,
                timestamp: Date.now(),
                stepId: 1,
              },
            ],
            nextStepId: 'address-input',
          };
        }
        return {
          messages: [
            {
              role: 'user',
              content: `Choose an address for **${label}**.`,
              timestamp: Date.now(),
              stepId: 1,
            },
          ],
          nextStepId: 'address-list',
          nextStepWidgetData: { addressOptions: opts },
        };
      }

      if (fg !== 'replyToAddress') return {};

      if (ctx.directInput) {
        return {
          messages: [
            { role: 'user', content: 'Enter **Reply-to address** (Singapore registered address format).', timestamp: Date.now(), stepId: 1 },
          ],
          nextStepId: 'address-input',
        };
      }

      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      if (opts.length <= 1) {
        return {
          messages: [{ role: 'user', content: 'Enter **Reply-to address** (Singapore).', timestamp: Date.now(), stepId: 1 }],
          nextStepId: 'address-input',
        };
      }
      return {
        messages: [
          { role: 'user', content: `Choose an address for **${ctx.subjectLabel || 'Reply-to address'}**.`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts },
      };
    }

    if (eventName === 'entity-field-select' && context.currentStepId === 'address-input') {
      const value = typeof payload === 'string' ? payload : payload?.value || payload?.address || '';
      if (!value || !String(value).trim()) return {};
      return {
        messages: [{ role: 'user', content: `Entered: **${String(value).trim()}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${opt.label || opt.type || 'Address'}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'base-code-select' && context.currentStepId === 'jurisdiction-b15') {
      const p = payload || {};
      const code = String(p.totalCode || '').trim();
      const name = String(p.codeName || p.title || '').trim() || code;
      return {
        messages: [
          {
            role: 'user',
            content: code ? `Selected jurisdiction: **${name}** (${code})` : `Selected jurisdiction: **${name}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'base-code-select' && context.currentStepId === 'country-code-b05') {
      const p = payload || {};
      const code = String(p.totalCode || '').trim();
      const name = String(p.codeName || p.title || '').trim() || code;
      return {
        messages: [
          {
            role: 'user',
            content: code ? `Selected country code: **${name}** (${code})` : `Selected country code: **${name}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'select-controller') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected controller';
      return {
        messages: [
          { role: 'user', content: `Selected controller: **${officerName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'select-secretary') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected secretary';
      return {
        messages: [
          { role: 'user', content: `Selected secretary: **${officerName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'select-ack-signer') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected signer';
      return {
        messages: [
          { role: 'user', content: `Selected acknowledgement signer: **${officerName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
